import { convertVietnameseToEnglish, stringify, uniqByKey } from '@/helpers/string';
import { dataSettingsSelector, locationInfoSelector } from '@/redux/slice/settings.slice';
import { IConditionValue, IRule } from '@/types/newSetup.type';
import { SkeletonBodyText } from '@shopify/polaris';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PreviewMethod from './PreviewMethod';
import { LocationConditionEnum } from '@/constants/enum';
import previewSlice from '@/redux/slice/preview.slice';

interface IProps {
  rules: IRule[];
  isAutoDetect?: boolean;
}

const PreviewCountry = (props: IProps) => {
  const dispatch = useDispatch();
  const dataSettings = useSelector(dataSettingsSelector);
  const locationInfo = useSelector(locationInfoSelector);
  const countryRules = props.rules.filter((rule) => rule.locationConditions?.find((condition) => condition.type === 'country'));
  const stateRules = props.rules.filter((rule) => rule.locationConditions?.find((condition) => condition.type === 'state'));
  const [stateSelected, setStateSelected] = useState('');
  const [countrySelected, setCountrySelected] = useState<any>('');
  const [selected, setSelected] = useState<IRule[]>([]);
  const [isInit, setIsInit] = useState(true);

  const isShowDefaultStateOption = useMemo(() => {
    const currentSelectedCountries = countryRules.filter((rule) =>
      rule?.locationConditions
        ?.find((condition) => condition.type === 'country')
        ?.value?.map((c) => c.data)
        ?.includes(countrySelected),
    );
    return currentSelectedCountries?.some(
      (item) => !item.locationConditions.some((lc) => lc.type === LocationConditionEnum.State),
    );
  }, [countryRules, countrySelected]);

  const baseRules = useMemo(() => {
    return props.rules.filter(
      (rule) =>
        !rule?.locationConditions?.find((condition) => condition?.type === 'zipcode') &&
        !rule?.locationConditions?.find((condition) => condition?.type === 'country'),
    );
  }, [props.rules]);

  const handleFlatFilterMap = useCallback((rule: (IConditionValue | undefined)[]) => {
    return rule
      .flat()
      .filter((i) => !!i)
      .map((c) => {
        return { value: c?.data, label: c?.countryStateName };
      });
  }, []);

  const countryOptions = useMemo(() => {
    const temp = countryRules.map((rule) => {
      const country = rule.locationConditions?.find((condition) => condition.type === 'country')?.value;
      if (!country) return undefined;
      return country;
    });
    return handleFlatFilterMap(temp as (IConditionValue | undefined)[]);
  }, [countryRules, handleFlatFilterMap]);

  const stateOptions = useMemo(() => {
    const currentStates = stateRules.filter((rule) => {
      return rule.locationConditions
        ?.find((condition) => condition.type === 'country')
        ?.value?.map((c) => c.data)
        ?.includes(countrySelected);
    });
    const temp = currentStates.map((rule) => {
      return rule.locationConditions?.find((condition) => condition.type === 'state')?.value;
    });

    const options = handleFlatFilterMap(temp as (IConditionValue | undefined)[]);
    return uniqByKey(options, 'value');
  }, [stateRules, handleFlatFilterMap, countrySelected]);

  const isShowSelectProvince = useMemo(() => {
    return stateOptions.length > 0;
  }, [stateOptions]);

  const countryName = useMemo(() => {
    const state = stateOptions.find((option) => option.value === stateSelected)?.label;
    const country = countryOptions.find((option) => option.value === countrySelected)?.label;
    return state ? `${state}, ${country}` : country;
  }, [countryOptions, countrySelected, stateOptions, stateSelected]);

  const handleChangeSelected = useCallback(
    ({ country, state }: { country: string; state: string }) => {
      let temp = props.rules.filter((rule) => {
        return (
          rule.locationConditions
            ?.find((condition) => condition.type === 'country')
            ?.value?.map((c) => c.data)
            ?.includes(country) &&
          (state
            ? rule.locationConditions
                ?.find((condition) => condition.type === 'state')
                ?.value?.map((c) => c.data)
                ?.includes(state)
            : true)
        );
      });
      if (!state)
        temp = temp.filter((rule) => rule?.locationConditions?.length === 1 && rule?.locationConditions[0].type === 'country');
      setSelected([...baseRules, ...temp]);
    },
    [baseRules, props.rules],
  );

  useEffect(() => {
    // auto detect state
    if (props.isAutoDetect && locationInfo?.data?.region_name) {
      const state = stateOptions?.find(
        (option) =>
          convertVietnameseToEnglish(option.label).toLowerCase() ===
          convertVietnameseToEnglish(locationInfo?.data?.region_name).toLowerCase(),
      );
      if (state) {
        setStateSelected(state.value);
        return;
      }
    }

    // select first state
    if (
      (!isShowDefaultStateOption && !stateSelected && stateOptions?.length) ||
      (stateOptions?.length && stateSelected && !stateOptions?.some((option) => option.value === stateSelected))
    ) {
      setStateSelected(stateOptions[0]?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowDefaultStateOption, locationInfo?.data?.region_name, props.isAutoDetect, stringify(stateOptions)]);

  useEffect(() => {
    let currentState = stateSelected;
    if (stateSelected && !stateOptions.some((option) => option.value === stateSelected)) {
      currentState = '';
    }
    handleChangeSelected({ country: countrySelected, state: currentState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countrySelected, stateSelected, handleChangeSelected, JSON.stringify(stateOptions)]);

  useEffect(() => {
    if (!selected.length && isInit && countrySelected) {
      const temp = props.rules.filter((rule) => {
        return (
          rule.locationConditions?.length === 1 &&
          rule.locationConditions
            ?.find((condition) => condition.type === 'country')
            ?.value?.map((c) => c.data)
            ?.includes(countrySelected) &&
          (stateSelected
            ? rule.locationConditions
                ?.find((condition) => condition.type === 'state')
                ?.value?.map((c) => c.data)
                ?.includes(stateSelected)
            : true)
        );
      });
      setSelected([...baseRules, ...temp]);
      setIsInit(false);
    }
  }, [props.rules, selected, countrySelected, stateSelected, isInit, baseRules]);

  useEffect(() => {
    // auto detect country
    if (
      props.isAutoDetect &&
      locationInfo?.data?.country_code &&
      countryOptions?.some((option) => option.value === locationInfo?.data?.country_code)
    ) {
      setCountrySelected(locationInfo?.data?.country_code);
      return;
    }

    // select first country
    if (countryOptions.length) {
      if (!countrySelected || (countrySelected && !countryOptions?.some((option) => option.value === countrySelected))) {
        setCountrySelected(countryOptions[0].value);
      }
    }
  }, [countryOptions, countrySelected, locationInfo?.data?.country_code, props.isAutoDetect]);

  useEffect(() => {
    dispatch(
      previewSlice.actions.handleSetSelectedCountry({
        country: countryName,
        flag: countrySelected,
      }),
    );
    return () => {
      dispatch(previewSlice.actions.handleSetSelectedCountry(undefined));
    };
  }, [countryName, countrySelected, dispatch]);

  return (
    <>
      {
        <>
          {countrySelected ? (
            <>
              <div className="ot-estimated-shipping-country-select-box">
                <select
                  className={`ot-estimated-shipping-country-select-box-country ${
                    !isShowSelectProvince ? 'ot-estimated-shipping-country-select-box-country-max-width' : ''
                  }`}
                  value={countrySelected}
                  onChange={(e) => {
                    setCountrySelected(e.target.value);
                    setStateSelected('');
                  }}
                >
                  {uniqByKey(
                    countryOptions?.filter((option) => option.value),
                    'value',
                  )
                    ?.sort((a, b) => a?.label?.localeCompare(b?.label))
                    ?.map((option) => {
                      if (!option) return null;
                      return (
                        <option value={option.value} key={option.value}>
                          {option.label}
                        </option>
                      );
                    })}
                </select>

                {isShowSelectProvince ? (
                  <select
                    className="ot-estimated-shipping-country-select-box-province"
                    value={stateSelected}
                    onChange={(e) => {
                      setStateSelected(e.target.value);
                    }}
                  >
                    {isShowDefaultStateOption ? <option value={''}>---</option> : null}
                    {stateOptions
                      ?.sort((a, b) => a.label.localeCompare(b.label))
                      ?.map((option) => {
                        return (
                          <option value={option.value} key={option.value}>
                            {option.label}
                          </option>
                        );
                      })}
                  </select>
                ) : null}
              </div>

              <div className="ot-estimated-shipping-country-layout">
                {dataSettings.enable_eta_information ? (
                  <>
                    {countrySelected ? (
                      <PreviewMethod
                        rules={selected}
                        country={countryName}
                        flag={countrySelected}
                        isEmptyRule={props.rules.length === 0}
                      />
                    ) : null}
                  </>
                ) : null}
              </div>
            </>
          ) : (
            <SkeletonBodyText lines={10} />
          )}
        </>
      }
    </>
  );
};

export default memo(PreviewCountry);
